// https://github.com/iamhosseindhv/notistack/issues/30#issuecomment-542863653
// Allows use to access the snackbar outside components (ie inside Apollo error handlers)
import { useSnackbar } from 'notistack'
import React from 'react'

const InnerSnackbarConfigurator = props => {
  const snackbarRef = useSnackbar();
  props.setUseSnackbarRef(snackbarRef);

  // Check for queued messages from previous page load/reload
  React.useEffect(() => {
    const queuedMessage = sessionStorage.getItem(QUEUED_MESSAGE_KEY);
    if (queuedMessage) {
      try {
        const { message, variant } = JSON.parse(queuedMessage);
        // Small delay to ensure snackbar is fully initialized
        setTimeout(() => {
          snackbarRef.enqueueSnackbar(message, { variant: variant || 'default' });
        }, 100);
      } catch (e) {
        console.error('Failed to parse queued snackbar message:', e);
      } finally {
        // Always clear the queued message
        sessionStorage.removeItem(QUEUED_MESSAGE_KEY);
      }
    }
  }, [snackbarRef]);

  return null;
}

let useSnackbarRef = useSnackbar;
const setUseSnackbarRef = useSnackbarRefProp => {
  useSnackbarRef = useSnackbarRefProp;
}

export const SnackbarConfigurator = () => {
  return <InnerSnackbarConfigurator setUseSnackbarRef={setUseSnackbarRef} />
}

const QUEUED_MESSAGE_KEY = 'queuedSnackbarMessage';

export const SnackbarKeys = Object.freeze({
  BACKGROUND_CSV_EXPORT: 'background-csv-export',
});

const Snackbar = {
  keys: SnackbarKeys,
  close(key) {
    useSnackbarRef.closeSnackbar(key);
  },
  success(message, options) {
    this.toast(message, { variant: 'success', ...options })
  },
  warning(message, options) {
    this.toast(message, { variant: 'warning', ...options })
  },
  info(message, options) {
    this.toast(message, { variant: 'info', ...options })
  },
  error(message, options) {
    this.toast(message, { variant: 'error', ...options })
  },
  toast(message, { variant, ...rest }) {
    useSnackbarRef.enqueueSnackbar(message, { variant: variant || "default", ...rest })
  },
  /**
   * Queue a message to be shown after page reload.
   * Useful when you need to reload the page but want to show a success message.
   *
   * @param {string} message - The message to display
   * @param {string} variant - The variant (success, error, warning, info, default)
   */
  queueMessage(message, variant = 'default') {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem(QUEUED_MESSAGE_KEY, JSON.stringify({
        message,
        variant
      }));
    }
  },
  /**
   * Queue a success message to be shown after page reload
   */
  queueSuccess(message) {
    this.queueMessage(message, 'success');
  },
  /**
   * Queue an error message to be shown after page reload
   */
  queueError(message) {
    this.queueMessage(message, 'error');
  },
  /**
   * Queue a warning message to be shown after page reload
   */
  queueWarning(message) {
    this.queueMessage(message, 'warning');
  },
  /**
   * Queue an info message to be shown after page reload
   */
  queueInfo(message) {
    this.queueMessage(message, 'info');
  },
}

export default Snackbar;
